/**
 * Required Document API Service
 */

import { apiRequest } from "../../api/apiClient";
import type {
  RequiredDocument,
  CreateRequiredDocumentDto,
  UpdateRequiredDocumentDto,
  RequiredDocumentByModuleItem,
} from "../../types/requiredDocument.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorText = await response.text().catch(() => "");
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

    if (errorText) {
      try {
        const parsed = JSON.parse(errorText);
        errorMessage = parsed.message || parsed.title || errorMessage;
      } catch {
        errorMessage = errorText;
      }
    }

    throw new Error(errorMessage);
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text) as T;
  } catch {
    return {} as T;
  }
};

export const requiredDocumentService = {
  getRequiredDocuments: async (): Promise<RequiredDocument[]> => {
    const response = await apiRequest("/RequiredDocuments", {
      method: "GET",
    });
    return handleResponse<RequiredDocument[]>(response);
  },

  getRequiredDocumentsByModule: async (
    moduleId: number,
    certificateApplicationId: number,
  ): Promise<RequiredDocumentByModuleItem[]> => {
    const response = await apiRequest(
      `/RequiredDocuments/by-module/${moduleId}/${certificateApplicationId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<RequiredDocumentByModuleItem[]>(response);
  },

  createRequiredDocument: async (
    data: CreateRequiredDocumentDto,
  ): Promise<RequiredDocument> => {
    const response = await apiRequest("/RequiredDocuments", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<RequiredDocument>(response);
  },

  updateRequiredDocument: async (
    id: number,
    data: UpdateRequiredDocumentDto,
  ): Promise<RequiredDocument> => {
    const response = await apiRequest(`/RequiredDocuments/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<RequiredDocument>(response);
  },

  deleteRequiredDocument: async (id: number): Promise<void> => {
    const response = await apiRequest(`/RequiredDocuments/${id}`, {
      method: "DELETE",
    });
    await handleResponse<void>(response);
  },
};
